import type { NavigationGuardWithThis, RouteLocationRaw } from 'vue-router'
import { useAuthStore } from '@/store/auth'

/**
 * Global router guard. Reads from the auth Pinia store (populated by `init()` in main.ts
 * before mount, so it is always settled on the very first navigation).
 *
 * Rules:
 * - `/signin` is always reachable (it's the login UI and the Auth0 callback handler).
 * - `/` redirects by auth status: authenticated → `/status`, otherwise → `/signin`.
 * - Routes with `meta.requiresAuth: true` require `status === 'authenticated'`; otherwise
 *   redirect to `/signin?next=<original path>` so we can return after login.
 * - Other routes are public (none currently).
 */
export const authGuard: NavigationGuardWithThis<undefined> = (to) => {
	const auth = useAuthStore()

	if (to.path === '/signin') return true

	if (to.path === '/') {
		return auth.isAuthenticated ? '/status' : '/signin'
	}

	if (to.meta.requiresAuth) {
		if (auth.isAuthenticated) return true
		const redirect: RouteLocationRaw = {
			path: '/signin',
			query: to.fullPath && to.fullPath !== '/' ? { next: to.fullPath } : {},
		}
		return redirect
	}

	return true
}
